import { useEffect, useRef, useState } from 'preact/hooks';
import type { HoldingRow } from '../../shared/types';
import { apiPatch, apiDelete, ApiError } from '../lib/api';
import {
  PositionLegsEditor,
  legsFromHolding,
  parseLegDrafts,
  type LegDraft,
} from './PositionLegsEditor';
import './../styles/components/edit-popover.css';

type Props = {
  row: HoldingRow;
  onClose: () => void;
  onSaved: () => void;
};

export function EditHoldingPopover({ row, onClose, onSaved }: Props) {
  const isStablecoin = row.asset_class === 'cash_eq';
  const [legs, setLegs] = useState<LegDraft[]>(() =>
    legsFromHolding({
      positions: row.positions,
      quantity: row.quantity,
      avgPrice: row.avg_price,
    })
  );
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setConfirmRemove(false);

    const parsed = parseLegDrafts(legs, { cashEq: isStablecoin });
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }

    setSaving(true);
    try {
      await apiPatch(
        `/api/portfolio/holdings/${encodeURIComponent(row.bucket_id)}/${encodeURIComponent(row.symbol)}`,
        { positions: parsed.legs }
      );
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Save failed';
      setError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (saving) return;
    if (!confirmRemove) {
      setConfirmRemove(true);
      setError(null);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await apiDelete(
        `/api/portfolio/holdings/${encodeURIComponent(row.bucket_id)}/${encodeURIComponent(row.symbol)}`
      );
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Remove failed';
      setError(msg || 'Remove failed');
      setConfirmRemove(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      class={`edit-modal${legs.length > 1 ? ' edit-modal-wide' : ''}`}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) dialogRef.current?.close();
      }}
    >
      <form class="edit-pop" onSubmit={handleSubmit}>
        <h3 class="edit-pop-title">
          Edit {row.symbol}
          <span class="edit-pop-subtitle">{row.name}</span>
        </h3>
        <PositionLegsEditor
          legs={legs}
          cashEq={isStablecoin}
          ccyLabel={row.ccy}
          saving={saving}
          onChange={(next) => {
            setLegs(next);
            setConfirmRemove(false);
          }}
        />
        {error && <div class="edit-pop-error mono">{error}</div>}
        <div class="edit-pop-actions edit-pop-actions-spread">
          <button
            type="button"
            class={`edit-pop-btn ${confirmRemove ? 'edit-pop-btn-danger' : 'edit-pop-btn-ghost'}`}
            onClick={handleRemove}
            disabled={saving}
          >
            {saving && confirmRemove
              ? 'Removing…'
              : confirmRemove
                ? 'Confirm remove'
                : 'Remove'}
          </button>
          <div class="edit-pop-actions-right">
            <button
              type="button"
              class="edit-pop-btn edit-pop-btn-ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" class="edit-pop-btn edit-pop-btn-primary" disabled={saving}>
              {saving && !confirmRemove ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </dialog>
  );
}
